/**
 * DrivePhotosPanel — Google Drive folder 経由の写真取込 (Sprint BG-DataSettings 20、GAP-A3 close)。
 *
 * 旧 v1 admin の Drive 写真連携 (F28 importDrivePhotos + F29 relinkDrivePhotos) を v2 で復活。
 * 2 mode:
 *   - 「新規 import」: postId と filename を CSV ペースト → importDrivePhotos callable で
 *     Storage に保存 + photoPath/photoUrl 等を返す。返値は results map (postId → photo URL)。
 *   - 「relink (既存 post)」: extraFields.originalId と filename を CSV ペースト →
 *     relinkDrivePhotos callable で post 検索 → Drive 写真を紐付け。
 *
 * 入力フォーマット (CSV、header 必須):
 *   - import: `postId,filename` (例: `p-abc,photo1.jpg`)
 *   - relink: `originalId,filename`
 *
 * 認可: master role 専用 (callable 側で deny、UI 側でも render を分岐)。
 */

import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/** functions/src/admin/importDrivePhotos.ts の入出力 shape (CLAUDE.md boundary 規約で local 宣言)。 */
interface ImportDrivePhotosInput {
  districtId: string;
  folderId: string;
  postIdToFilename: Record<string, string>;
}
interface PhotoUploadEntry {
  photoPath: string;
  photoUrl: string;
  photoThumbPath: string;
  photoThumbUrl: string;
}
interface ImportDrivePhotosResult {
  ok: true;
  uploaded: number;
  failed: string[];
  results: Record<string, PhotoUploadEntry>;
}

interface RelinkEntry {
  originalId: string;
  photoFilename: string;
}
interface RelinkDrivePhotosInput {
  districtId: string;
  folderId: string;
  entries: RelinkEntry[];
}
interface RelinkDrivePhotosResult {
  ok: true;
  linked: number;
  skipped: number;
  failed: string[];
}

export interface DrivePhotosPanelProps {
  districtId: string;
  /** false の場合、入力 / button を全て disabled。 */
  enabled: boolean;
}

const DRIVE_FOLDER_URL_REGEX = /\/folders\/([a-zA-Z0-9_-]+)/;

/** Drive URL から folderId 抽出 (URL 直貼り対応、ID 直接入力もそのまま受領)。 */
export function extractFolderId(input: string): string {
  const m = DRIVE_FOLDER_URL_REGEX.exec(input);
  return m?.[1] ?? input.trim();
}

/** "key,filename" の 2 列 CSV を Record<key, filename> に。先頭行は header 期待 (postId/originalId,filename)。 */
export function parseFilenameMapping(text: string): {
  ok: boolean;
  entries: { key: string; filename: string }[];
  error?: string;
} {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return {
      ok: false,
      entries: [],
      error: 'header 行 + 1 行以上のデータが必要です (例: postId,filename)',
    };
  }
  // header skip
  const entries: { key: string; filename: string }[] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i]!.split(',').map((p) => p.trim());
    if (parts.length < 2 || !parts[0] || !parts[1]) continue;
    entries.push({ key: parts[0]!, filename: parts[1]! });
  }
  if (entries.length === 0) {
    return { ok: false, entries: [], error: '有効なデータ行が 0 件です' };
  }
  return { ok: true, entries };
}

type Mode = 'import' | 'relink';

export function DrivePhotosPanel({ districtId, enabled }: DrivePhotosPanelProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<Mode>('import');
  const [folderUrl, setFolderUrl] = useState('');
  const [csvMapping, setCsvMapping] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportDrivePhotosResult | null>(null);
  const [relinkResult, setRelinkResult] = useState<RelinkDrivePhotosResult | null>(null);

  function reset() {
    setError(null);
    setImportResult(null);
    setRelinkResult(null);
  }

  async function handleSubmit() {
    reset();
    const folderId = extractFolderId(folderUrl);
    if (!folderId) {
      setError('Drive フォルダ URL または ID が空です');
      return;
    }
    const parsed = parseFilenameMapping(csvMapping);
    if (!parsed.ok) {
      setError(parsed.error ?? 'parse 失敗');
      return;
    }
    setSubmitting(true);
    try {
      if (mode === 'import') {
        const map: Record<string, string> = {};
        for (const e of parsed.entries) map[e.key] = e.filename;
        const callable = httpsCallable<ImportDrivePhotosInput, ImportDrivePhotosResult>(
          functions,
          'importDrivePhotos',
        );
        const res = await callable({ districtId, folderId, postIdToFilename: map });
        setImportResult(res.data);
        toast('success', `Drive: ${res.data.uploaded} 件 upload`);
      } else {
        const entries: RelinkEntry[] = parsed.entries.map((e) => ({
          originalId: e.key,
          photoFilename: e.filename,
        }));
        const callable = httpsCallable<RelinkDrivePhotosInput, RelinkDrivePhotosResult>(
          functions,
          'relinkDrivePhotos',
        );
        const res = await callable({ districtId, folderId, entries });
        setRelinkResult(res.data);
        toast('success', `Drive relink: ${res.data.linked} 件 紐付け`);
      }
    } catch (e) {
      const msg = (e as Error).message ?? String(e);
      setError(`Drive 連携失敗: ${msg}`);
      toast('error', `Drive 連携失敗`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card padding="lg" className="mb-4">
      <h2 className="text-base font-bold text-slate-900 mb-3">
        Drive 写真連携 (F28 / F29、GAP-A3)
      </h2>
      <p className="text-xs text-slate-500 mb-3">
        Service Account email に閲覧共有した Drive フォルダから写真を取込。「新規 import」は
        callable 経由で Storage に保存 (postId 指定)、「relink」は既存 post の
        <code> extraFields.originalId </code>に対して紐付け。
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        {[
          { value: 'import' as const, label: '新規 import (postId,filename)' },
          { value: 'relink' as const, label: 'relink (originalId,filename)' },
        ].map((m) => (
          <label
            key={m.value}
            className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
          >
            <input
              type="radio"
              name="drive-mode"
              value={m.value}
              checked={mode === m.value}
              onChange={() => {
                setMode(m.value);
                reset();
              }}
              disabled={!enabled}
            />
            {m.label}
          </label>
        ))}
      </div>

      <div className="mb-2">
        <label htmlFor="drive-folder-url" className="block text-xs text-slate-600 mb-1">
          Drive フォルダ URL or ID
        </label>
        <input
          id="drive-folder-url"
          type="text"
          value={folderUrl}
          onChange={(e) => setFolderUrl(e.target.value)}
          disabled={!enabled || submitting}
          placeholder="https://drive.google.com/drive/folders/<id> or <id>"
          className="w-full min-h-touch px-3 border border-slate-300 rounded text-sm font-mono"
        />
      </div>

      <div className="mb-2">
        <label htmlFor="drive-csv-mapping" className="block text-xs text-slate-600 mb-1">
          マッピング CSV (header + データ行)
        </label>
        <textarea
          id="drive-csv-mapping"
          value={csvMapping}
          onChange={(e) => setCsvMapping(e.target.value)}
          disabled={!enabled || submitting}
          rows={6}
          placeholder={
            mode === 'import'
              ? 'postId,filename\np-001,photo1.jpg\np-002,photo2.jpg'
              : 'originalId,filename\norig-001,photo1.jpg\norig-002,photo2.jpg'
          }
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono"
        />
      </div>

      <Button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={!enabled || submitting || !folderUrl.trim() || !csvMapping.trim()}
      >
        {submitting ? '処理中…' : mode === 'import' ? 'Drive 写真を import' : 'Drive 写真を relink'}
      </Button>

      {error && (
        <p className="text-sm text-danger-700 mt-3" role="alert">
          {error}
        </p>
      )}

      {importResult && (
        <div className="mt-3 text-sm">
          <p className="text-success-700 font-semibold">
            完了: {importResult.uploaded} 件 upload / {importResult.failed.length} 件 失敗
          </p>
          {importResult.failed.length > 0 && (
            <ul className="text-xs text-danger-700 list-disc pl-5 mt-2 max-h-48 overflow-auto">
              {importResult.failed.slice(0, 50).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {relinkResult && (
        <div className="mt-3 text-sm">
          <p className="text-success-700 font-semibold">
            relink 完了: {relinkResult.linked} 件 紐付け / {relinkResult.skipped} 件 skip /{' '}
            {relinkResult.failed.length} 件 失敗
          </p>
          {relinkResult.failed.length > 0 && (
            <ul className="text-xs text-danger-700 list-disc pl-5 mt-2 max-h-48 overflow-auto">
              {relinkResult.failed.slice(0, 50).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
